import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { CopyLifecycleActions } from "@/components/catalog/CopyLifecycleActions";
import { ExistingCopyEditorFields } from "@/components/catalog/ExistingCopyEditorFields";
import { WithdrawnCopyBanner } from "@/components/catalog/WithdrawnCopyBanner";
import type { CatalogItemCopy } from "@/lib/api/catalog";
import type { ItemType } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { Collection } from "@/lib/api/collections";
import type { BookCondition } from "@/lib/api/book-conditions";
import type { ItemStatus } from "@/lib/api/item-statuses";
import type { Branch } from "@/lib/api/branches";
import { Save } from "lucide-react";
import { formatAccessionNumber, formatNumberWithCommas } from "@/lib/utils/catalog-formatting";

interface ExistingCopyEditorProps {
  item: CatalogItemCopy;
  branches: Branch[];
  itemTypes: ItemType[];
  locations: Location[];
  collections: Collection[];
  conditions: BookCondition[];
  itemStatuses: ItemStatus[];
  savingItemId: string | null;
  canEdit: boolean;
  canDelete: boolean;
  onSave: (updates: Record<string, unknown>) => Promise<void>;
  onItemUpdated: (updated: CatalogItemCopy) => void;
  onItemRemoved: (itemId: string) => void;
}

export function ExistingCopyEditor({
  item,
  branches,
  itemTypes,
  locations,
  collections,
  conditions,
  itemStatuses,
  savingItemId,
  canEdit,
  canDelete,
  onSave,
  onItemUpdated,
  onItemRemoved,
}: ExistingCopyEditorProps) {
  const effectiveBranchId = item.branchId || item.branch?.id || "";
  const [branchId, setBranchId] = useState(effectiveBranchId);
  const [locationId, setLocationId] = useState(item.locationId || "");
  const [collectionId, setCollectionId] = useState(item.collectionId || "");
  const [itemTypeId, setItemTypeId] = useState(item.itemTypeId || "");
  const [conditionCode, setConditionCode] = useState(item.conditionCode || "");
  const [statusCode, setStatusCode] = useState(item.statusCode || "AVAILABLE");
  const effectiveAutoRenewalDisabled = Boolean(item.autoRenewalDisabled);
  const [autoRenewalDisabled, setAutoRenewalDisabled] = useState(effectiveAutoRenewalDisabled);

  const isWithdrawn = item.statusCode === "WITHDRAWN" || Boolean(item.withdrawnAt);
  const fieldsDisabled = isWithdrawn || !canEdit;

  useEffect(() => {
    setBranchId(item.branchId || item.branch?.id || "");
    setLocationId(item.locationId || "");
    setCollectionId(item.collectionId || "");
    setItemTypeId(item.itemTypeId || "");
    setConditionCode(item.conditionCode || "");
    setStatusCode(item.statusCode || "AVAILABLE");
    setAutoRenewalDisabled(Boolean(item.autoRenewalDisabled));
  }, [
    item.id,
    item.branchId,
    item.branch?.id,
    item.locationId,
    item.collectionId,
    item.itemTypeId,
    item.conditionCode,
    item.statusCode,
    item.autoRenewalDisabled,
  ]);

  const hasChanges =
    !fieldsDisabled &&
    (branchId !== effectiveBranchId ||
      locationId !== (item.locationId || "") ||
      collectionId !== (item.collectionId || "") ||
      itemTypeId !== (item.itemTypeId || "") ||
      conditionCode !== (item.conditionCode || "") ||
      statusCode !== (item.statusCode || "AVAILABLE") ||
      autoRenewalDisabled !== effectiveAutoRenewalDisabled);

  const handleSave = () => {
    onSave({
      branchId: branchId || null,
      locationId: locationId || null,
      collectionId: collectionId || null,
      itemTypeId: itemTypeId || null,
      conditionCode: conditionCode || null,
      statusCode,
      autoRenewalDisabled,
    });
  };

  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-gray-900">{item.barcode}</span>
          <span className="text-xs text-gray-500">
            Acc. #{formatAccessionNumber(item.accessionNumber) || item.accessionNumber} · Copy #
            {formatNumberWithCommas(item.copyNumber)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasChanges && (
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={savingItemId === item.id}
              className="gap-1"
            >
              {savingItemId === item.id ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </Button>
          )}
          <CopyLifecycleActions
            item={item}
            canEdit={canEdit}
            canDelete={canDelete}
            onUpdated={onItemUpdated}
            onRemoved={onItemRemoved}
          />
        </div>
      </div>

      {isWithdrawn && <WithdrawnCopyBanner item={item} />}

      <ExistingCopyEditorFields
        itemId={item.id}
        disabled={fieldsDisabled}
        branchId={branchId}
        locationId={locationId}
        collectionId={collectionId}
        itemTypeId={itemTypeId}
        conditionCode={conditionCode}
        statusCode={statusCode}
        autoRenewalDisabled={autoRenewalDisabled}
        branches={branches}
        itemTypes={itemTypes}
        locations={locations}
        collections={collections}
        conditions={conditions}
        itemStatuses={itemStatuses}
        onBranchChange={setBranchId}
        onLocationChange={setLocationId}
        onCollectionChange={setCollectionId}
        onItemTypeChange={setItemTypeId}
        onConditionChange={setConditionCode}
        onStatusChange={setStatusCode}
        onAutoRenewalChange={setAutoRenewalDisabled}
      />
    </div>
  );
}
